<template>
    <!-- 포인트 상세 수정/취소하기 Modal -->
    <BaseConfirm
        style="width: 780px"
        :isOpen="isOpen"
        :title="`포인트 ${serviceTypeKor} 요청 수정/취소하기`"
        :isShowCancelBtn="false"
        :ignore-backdrop-click="true"
        :confirmText="'요청 수정'"
        @show="showHandler"
        @accept="submitHandler"
        @cancel="closeHandler"
    >
        <SkeletonLoader v-if="isLoading" :height="skeletonHeight" />

        <template v-else>
            <PointRequestDetailPopupSummary
                :serviceType="serviceType"
                :serviceTypeKor="serviceTypeKor"
                v-bind="summaryData"
            />

            <div class="form-input-group">
                <p class="title">지급/회수 형태</p>
                <div class="contents font-bold">
                    {{
                        detailData.type
                            ? getServiceTypeKorName(detailData.type)
                            : '-'
                    }}
                </div>
            </div>

            <div class="form-input-group">
                <div class="title pt-7">
                    <p class="required">{{ serviceTypeKor }}사유</p>
                    <p class="caption">최대 50자까지 입력 가능</p>
                </div>
                <div class="contents">
                    <BaseInput
                        :name="'description'"
                        :placeholder="`예시: 2023년 상반기 복지포인트 ${serviceTypeKor}요청`"
                        :hasError="submitCount > 0 && !!errors.description"
                        v-model="description"
                    />
                </div>
            </div>

            <div
                v-if="serviceType === TargetServiceTypeE.PROVIDE"
                class="form-input-group"
            >
                <div class="title pt-7">
                    <p class="required">유효기간</p>
                    <p class="caption">현재날짜 이후 날짜로만 입력 가능</p>
                </div>
                <div class="contents">
                    <DatePicker
                        style="width: 290px"
                        :name="'expireDate'"
                        :minDate="'today'"
                        :placeholder="'유효기간을 선택해주세요.'"
                        :hasError="submitCount > 0 && !!errors.expireDate"
                        v-model="expireDate"
                        @destroyDatePicker="destroyDatePicker"
                    />
                </div>
            </div>

            <div
                v-if="serviceType === TargetServiceTypeE.PROVIDE"
                class="form-input-group"
            >
                <div
                    v-if="detailData.type === ServiceTypeE.RESERVE"
                    class="title pt-7"
                >
                    <p class="required">지급(예약)일시</p>
                    <div class="caption">
                        <p>
                            유효기간보다 이전 날짜 및<br />
                            분(minutes)은 10분 단위로만 입력 가능
                        </p>
                        <p style="margin-top: 3px">
                            ※ 예시：14:10, 09:00, 20:50
                        </p>
                    </div>
                </div>
                <div v-else class="title">지급(예약)일시</div>

                <div class="contents">
                    <DatePicker
                        v-if="detailData.type === ServiceTypeE.RESERVE"
                        style="width: 290px"
                        :name="'provideAt'"
                        :placeholder="'지급(예약)일시를 선택해주세요.'"
                        :enableTime="true"
                        :minDate="provideAtMinDate"
                        :minuteIncrement="10"
                        :hasError="submitCount > 0 && !!errors.provideAt"
                        v-model="provideAt"
                        @updateDate="onChangeProvideAt"
                        @destroyDatePicker="destroyDatePicker"
                    />

                    <template v-else>-</template>
                </div>
            </div>

            <div
                v-if="serviceType === TargetServiceTypeE.PROVIDE"
                class="form-input-group"
            >
                <div class="title pt-7">
                    <p class="required">미가입자 지급대기기간</p>
                    <p class="caption">유효기간보다 이전 날짜로만 입력 가능</p>
                </div>
                <div class="contents">
                    <DatePicker
                        style="width: 290px"
                        :name="'limitedAt'"
                        :minDate="'today'"
                        :maxDate="expireDate"
                        :placeholder="'미가입자 지급대기기간을 선택해주세요.'"
                        :hasError="submitCount > 0 && !!errors.limitedAt"
                        v-model="limitedAt"
                        @destroyDatePicker="destroyDatePicker"
                    />
                </div>
            </div>

            <div
                v-if="
                    detailData.status === RequestStatusE.CONFIRM ||
                    detailData.status === RequestStatusE.REJECT ||
                    detailData.status === RequestStatusE.PROCESSING ||
                    detailData.status === RequestStatusE.FINISHED
                "
                class="form-input-group"
            >
                <p class="title">승인/반려 사유</p>
                <div class="contents">
                    {{ detailData.approveDescription || '-' }}
                </div>
            </div>

            <div class="form-input-group">
                <p class="title">{{ serviceTypeKor }}대상</p>
                <div class="contents">
                    {{ detailData.uploadedFileName || '-' }}
                </div>
            </div>

            <div class="form-input-group">
                <p class="title">처리상태</p>
                <div class="contents font-bold">
                    {{
                        detailData.status
                            ? getRequestStatusKorName(detailData.status)
                            : '-'
                    }}
                </div>
            </div>
        </template>

        <template #footerButton>
            <BaseButton
                class="base-confirm-btn"
                type="button"
                :color="'danger'"
                @click="cancelHandler"
                >요청 취소</BaseButton
            >
        </template>
    </BaseConfirm>
</template>

<script setup lang="ts">
import {
    cancelPointRequest as cancelPointRequestApi,
    getPointProvideRequestDetail as getPointProvideRequestDetailApi,
    getPointRetrieveRequestDetail as getPointRetrieveRequestDetailApi,
    updatePointProvideRequestDetail as updatePointProvideRequestDetailApi,
    updatePointRetrieveRequest as updatePointRetrieveRequestApi,
} from '@/api/points/point-request-detail-api';
import BaseButton from '@/components/common/input/BaseButton.vue';
import BaseInput from '@/components/common/input/BaseInput.vue';
import DatePicker from '@/components/common/input/DatePicker.vue';
import SkeletonLoader from '@/components/common/layout/SkeletonLoader.vue';
import BaseConfirm from '@/components/common/popup/BaseConfirm.vue';
import PointRequestDetailPopupSummary from '@/components/points/request-detail/popup/PointRequestDetailPopupSummary.vue';
import {
    getRequestStatusKorName,
    getServiceTypeKorName,
} from '@/filter/points-filter';
import {useFieldModelCustom} from '@/hooks/vee-validate/useFieldModelCustom';
import useGlobalStore from '@/store/global-store';
import {PointRequestDetailResI} from '@/types/points/api/point-request-detail-api-types';
import {
    RequestStatusE,
    RequestStatusT,
    ServiceTypeE,
    ServiceTypeKorE,
    ServiceTypeKorT,
    ServiceTypeT,
    TargetServiceTypeE,
    TargetServiceTypeKorE,
    TargetServiceTypeT,
} from '@/types/points/point-keyword-types';
import {UpdatePointRequestDetailFormStateT} from '@/types/points/point-request-detail-types';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {DateTime} from 'luxon';
import {Instance} from 'modules/flatpickr/dist/types/instance';
import {storeToRefs} from 'pinia';
import {useForm} from 'vee-validate';
import {computed, onUnmounted, reactive} from 'vue';
import {formatDateTime24} from 'yeogi-utils/filters/date-time-filter';
import {object, string} from 'yup';

// props, emits
interface PropsI {
    isOpen: boolean;
    id: number;
    serviceType: TargetServiceTypeT | '';
    provideType: ServiceTypeKorT | '';
}

const props = withDefaults(defineProps<PropsI>(), {
    isOpen: false,
});

const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'refresh'): void;
    (e: 'destroyDatePicker'): void;
}>();

const global = useGlobalStore();

// computed
const serviceTypeKor = computed(() => {
    switch (props.serviceType) {
        case TargetServiceTypeE.PROVIDE:
            return TargetServiceTypeKorE.지급;
        case TargetServiceTypeE.RETRIEVE:
            return TargetServiceTypeKorE.회수;
        default:
            return '';
    }
});

const skeletonHeight = computed(() => {
    if (props.serviceType === 'RETRIEVE') {
        // 회수 수정 팝업
        return 400;
    } else {
        // 즉시지급/예약지급 수정 팝업
        return 650;
    }
});

const provideAtMinDate = computed(() => {
    // NOTE: 예약지급일시는 현재시간으로부터 30분 이후부터의 시간 & 10분 단위로만 저장 가능
    let minProvideDate = DateTime.now().plus({minutes: 30});
    const mins = minProvideDate.minute;

    if (mins % 10) {
        minProvideDate = minProvideDate.set({
            minute: 10 * Math.round(minProvideDate.minute / 10),
        });
    }

    return detailData.type === ServiceTypeE.RESERVE
        ? minProvideDate.toFormat('yyyy-MM-dd HH:mm')
        : 'today';
});

// libs
const {
    values: formState,
    setFieldValue,
    resetForm,
    handleSubmit,
    submitCount,
    errors,
} = useForm({
    initialValues: {
        description: '',

        // NOTE: 지급 수정 팝업에서만 사용
        expireDate: '',
        provideAt: '', // 예약지급 수정 팝업에서만 사용
        limitedAt: '',
    } as UpdatePointRequestDetailFormStateT,
    validationSchema: object({
        description: string()
            .required(`${serviceTypeKor.value}사유를 입력해주세요.`)
            .matches(
                /* eslint-disable no-useless-escape */
                /^(?!.*[@|#|%|&|*|\/|\\|~]).*(?=[a-zA-Z0-9ㄱ-힣]).*$/,
                `특수문자 @, #, %, &, *, /, ＼, ~는 사용할 수 없습니다.`,
            )
            .max(
                50,
                (description) =>
                    `${serviceTypeKor.value}사유가 너무 깁니다. ${serviceTypeKor.value}사유는 50자 이하까지 입력 가능합니다.\n※ 현재 ${serviceTypeKor.value}사유 길이: ${description.value.length}자`,
            ),
        expireDate: string().when(() => {
            // NOTE: 예약지급/즉시지급 수정 팝업일때만 validate
            if (
                props.provideType === ServiceTypeKorE.예약지급 ||
                props.provideType === ServiceTypeKorE.즉시지급
            ) {
                return string().required('유효기간을 선택해주세요.');
            }

            return string().notRequired();
        }),
        provideAt: string().when(() => {
            // NOTE: 예약지급 수정 팝업일때만 validate
            if (props.provideType === ServiceTypeKorE.예약지급) {
                return string().required('지급(예약)일시를 선택해주세요.');
            }

            return string().notRequired();
        }),
        limitedAt: string().when(() => {
            // NOTE: 예약지급/즉시지급 수정 팝업일때만 validate
            if (
                props.provideType === ServiceTypeKorE.예약지급 ||
                props.provideType === ServiceTypeKorE.즉시지급
            ) {
                return string().required(
                    '미가입자 지급대기기간을 선택해주세요.',
                );
            }

            return string().notRequired();
        }),
    }),
});

// Form state 참조 & 업데이트용
const {description, expireDate, provideAt, limitedAt} = useFieldModelCustom<{
    description: string;
    expireDate: string;
    provideAt: string;
    limitedAt: string;
}>(['description', 'expireDate', 'provideAt', 'limitedAt']);

// ref, reactive
const {isLoading} = storeToRefs(global);

const summaryData = reactive({
    uploadedCount: 0,
    successCount: 0,
    waitingCount: 0,
    failCount: 0,
    totalPoint: 0,
    totalSuccessPoint: 0,
});

// 상세 데이터 저장
const detailData = reactive({
    id: 0,
    description: '',
    expireDate: '',
    approveDescription: '',
    limitedAt: '',
    provideAt: '',
    type: '' as ServiceTypeT,
    uploadedFileName: '',
    status: '' as RequestStatusT,
});

// methods
const showHandler = async () => {
    try {
        // 조회 API
        let response = {} as PointRequestDetailResI;

        if (props.serviceType === TargetServiceTypeE.PROVIDE) {
            // 지급 요청 조회
            response = await getPointProvideRequestDetailApi(props.id);
        } else if (props.serviceType === TargetServiceTypeE.RETRIEVE) {
            // 회수 요청 조회
            response = await getPointRetrieveRequestDetailApi(props.id);
        }

        // summaryData 저장. props 전달용
        summaryData.uploadedCount = response.uploadedCount;
        summaryData.successCount = response.successCount;
        summaryData.waitingCount = response.waitingCount ?? 0;
        summaryData.failCount = response.failCount;
        summaryData.totalPoint = response.totalPoint;
        summaryData.totalSuccessPoint = response.totalSuccessPoint;

        // 상세 데이터 저장
        detailData.approveDescription = response.approveDescription ?? '';
        detailData.description = response.description;
        detailData.expireDate = response.expireDate ?? '';
        detailData.id = response.id;
        detailData.limitedAt = response.limitedAt ?? '';
        detailData.provideAt = response.provideAt ?? '';
        detailData.status = response.status;
        detailData.type = response.type;
        detailData.uploadedFileName = response.uploadedFileName;

        // Form State 저장. validation용
        setFieldValue('description', response.description);

        if (props.serviceType === TargetServiceTypeE.PROVIDE) {
            // NOTE: 지급 수정 팝업용 state

            if (response.expireDate) {
                setFieldValue(
                    'expireDate',
                    formatDateTime24(response.expireDate),
                );
            }
            if (response.provideAt) {
                setFieldValue(
                    'provideAt',
                    `${formatDateTime24(response.provideAt, {hasTime: true})}`,
                );
            }
            if (response.limitedAt) {
                setFieldValue(
                    'limitedAt',
                    formatDateTime24(response.limitedAt),
                );
            }
        }
    } catch (err) {
        // 서버 에러
        const axiosError = err as AxiosError;
        window.customAlert(axiosError.message);
    }
};

const closeHandler = () => {
    resetForm();
    emits('close');
};

const submitHandler = handleSubmit(async () => {
    if (
        !confirm(
            `입력하신 내용대로 포인트 ${serviceTypeKor.value} 요청을 수정하시겠습니까?\n이대로 진행하시면, 포인트 ${serviceTypeKor.value} 요청이 수정(변경)됩니다.\n\n※ 변경 사항 없이 팝업창을 닫으시려면, 팝업창 우측 상단의 X를 클릭해 주세요.`,
        )
    )
        return;

    try {
        if (props.serviceType === TargetServiceTypeE.PROVIDE) {
            // 지급 요청 수정
            await updatePointProvideRequestDetailApi({
                id: props.id,
                description: formState.description,
                expireDate: formState.expireDate,
                reservedAt: formState.provideAt, // "2023-04-12T01:38:41.841Z"
                limitedAt: formState.limitedAt,
            });
        } else if (props.serviceType === TargetServiceTypeE.RETRIEVE) {
            // 회수 요청 수정
            await updatePointRetrieveRequestApi({
                id: props.id,
                description: formState.description,
            });
        }

        alert(`포인트 ${serviceTypeKor.value} 요청 수정이 완료되었습니다.`);
        closeHandler();

        emits('refresh');
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                header: axiosError.config.headers,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-detail/popup/PointRequestDetailEditPopup.vue > submitHandler',
            },
        });

        window.customAlert(axiosError.message);
    }
});

/**
 * @description 지급(예약)일시의 분(minutes) 값이 10분 단위가 아닐때, 반올림 처리후 10분 단위로 분(minutes)값 출력
 * 예약지급 배치가 10분 단위로 돌고 있기 때문에, 시각을 무조건 10분 단위로만 선택 가능하도록 대응
 * @param param
 */
const onChangeProvideAt = ({
    selectedDates,
    dateString,
    instance,
}: {
    selectedDates: Date[];
    dateString: string;
    instance: Instance;
}) => {
    setTimeout(() => {
        const latestSelectedDate = instance.latestSelectedDateObj as Date;
        const mins = latestSelectedDate.getMinutes();

        // NOTE: 입력받은 분(minutes)값을 10으로 나눴을 때, 나머지가 있으면 반올림 처리후 10분 단위로 분(minutes)값 출력
        if (mins % 10) {
            latestSelectedDate.setMinutes(
                10 * Math.round(latestSelectedDate.getMinutes() / 10),
            );
        }

        instance.setDate(latestSelectedDate, false); // DatePicker 값 업데이트
        setFieldValue(
            'provideAt',
            DateTime.fromJSDate(latestSelectedDate).toFormat(
                'yyyy-MM-dd HH:mm',
            ),
        );

        instance.redraw(); // 재출력
    }, 300); // 입력한대로 값이 화면에 안보이면 유저가 당황할 수 있기 때문에, 0.3s 후에 값 업데이트. 10분 단위로만 입력 가능하다는 것을 Form 주석에 추가해둠
};

const cancelHandler = async () => {
    if (
        !confirm(
            `포인트 ${serviceTypeKor.value} 요청을 취소하시겠습니까?\n이대로 진행하시면, 포인트 ${serviceTypeKor.value} 요청이 취소 처리가 됩니다.\n\n※ 변경 사항 없이 팝업창을 닫으시려면, 팝업창 우측 상단의 X를 클릭해 주세요.`,
        )
    )
        return;

    if (!props.id || !props.serviceType)
        return alert(
            `포인트 ${serviceTypeKor.value} 요청에 필요한 ID 값이 없습니다.`,
        );

    try {
        const serviceType = props.serviceType.toLowerCase() as
            | 'provide'
            | 'retrieve';
        await cancelPointRequestApi(serviceType, props.id);

        alert(`포인트 ${serviceTypeKor.value} 요청이 취소되었습니다.`);
        closeHandler();

        emits('refresh');
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                header: axiosError.config.headers,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-detail/popup/PointRequestDetailEditPopup.vue > cancelHandler',
            },
        });

        window.customAlert(axiosError.message);
    }
};

const destroyDatePicker = () => {
    emits('destroyDatePicker');
};

// effect
onUnmounted(() => {
    destroyDatePicker();
});
</script>

<style lang="scss" scoped>
.form-input-group {
    display: flex;

    & + & {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebebeb;
    }

    .title {
        position: relative;
        width: 25%;
        font-weight: 600;
        word-break: keep-all;

        &.required,
        .required {
            &::after {
                margin-left: 1px;
                content: '*';
                color: $danger;
                line-height: 100%;
                font-size: 10px;
                vertical-align: super;
            }
        }

        .caption {
            margin-top: 3px;
            color: $gray;
            font-size: 8px;
            font-weight: 500;
            line-height: 16px;
        }

        &.pt-7 {
            padding-top: 7px;
        }
    }

    .contents {
        flex: 1 75%;
    }
}
</style>
